import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  REGISTRATION_PAYMENT_LABELS,
  RSVP_BLOCKED_MESSAGES,
  type RsvpSummaryView,
  type WineCategoryView,
} from '@gwc/shared';
import {
  completeWine,
  draftFromView,
  isDraftEmpty,
  money,
  rsvpApi,
  shortDay,
  type RsvpBody,
  type StructuredWineDraft,
} from '../api/rsvp';
import { ApiError } from '../lib/api';
import { ImageInput } from './ImageInput';
import { StructuredWineFields } from './StructuredWineFields';
import { Button, Dialog } from './ui';

/**
 * The member's RSVP for one tasting: what they're bringing (structured, into
 * the Cellar), which category it fills, the label photo, paying for the seat,
 * and cancelling. Everything a phone needs, top to bottom, with one obvious
 * button.
 */
export function RsvpPanel({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const { data: summary, isLoading } = useQuery({
    queryKey: ['rsvp', eventId],
    queryFn: () => rsvpApi.summary(eventId),
  });
  const onChanged = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['rsvp', eventId] });
    void qc.invalidateQueries({ queryKey: ['rsvp-summary', eventId] });
    void qc.invalidateQueries({ queryKey: ['registrations', eventId] });
    void qc.invalidateQueries({ queryKey: ['attendees', eventId] });
  }, [qc, eventId]);

  if (isLoading || !summary) {
    return (
      <div className="card mb-6">
        <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">Your RSVP</h2>
        <p className="text-sm text-cream-300/60">Loading…</p>
      </div>
    );
  }
  return <RsvpPanelBody eventId={eventId} summary={summary} onChanged={onChanged} />;
}

function RsvpPanelBody({
  eventId,
  summary,
  onChanged,
}: {
  eventId: string;
  summary: RsvpSummaryView;
  onChanged: () => void;
}) {
  const [params, setParams] = useSearchParams();
  const [askCancel, setAskCancel] = useState(false);
  const live = summary.myStatus === 'registered' || summary.myStatus === 'waitlist';

  const [wine, setWine] = useState<StructuredWineDraft>(() => draftFromView(summary.myWine));
  const [categoryId, setCategoryId] = useState<string | null>(summary.myCategoryId);
  const [labelImageUrl, setLabelImageUrl] = useState(summary.myLabelImageUrl ?? '');
  const [notes, setNotes] = useState(summary.myNotes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  // Re-seed the form when the server's copy of the RSVP changes (after a save,
  // or an admin's correction) — not on every background refetch.
  const serverKey = [
    summary.registrationId,
    summary.myStatus,
    summary.myWine?.catalogId,
    summary.myWine?.display,
    summary.myCategoryId,
    summary.myLabelImageUrl,
    summary.myNotes,
  ].join('|');
  useEffect(() => {
    setWine(draftFromView(summary.myWine));
    setCategoryId(summary.myCategoryId);
    setLabelImageUrl(summary.myLabelImageUrl ?? '');
    setNotes(summary.myNotes ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  // Back from the card payment page. The webhook that marks the seat paid can
  // land a moment after the redirect, so look again for a little while.
  const paymentReturn = params.get('payment');
  const awaitingWebhook = paymentReturn === 'success' && summary.myPaymentStatus === 'unpaid';
  useEffect(() => {
    if (!awaitingWebhook) return;
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      onChanged();
      if (tries >= 10) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
  }, [awaitingWebhook, onChanged]);
  const dismissPaymentReturn = () => {
    params.delete('payment');
    setParams(params, { replace: true });
  };

  const save = useMutation({
    mutationFn: (body: RsvpBody) => rsvpApi.save(eventId, body),
    onSuccess: (res) => {
      setError(null);
      setErrorCode(null);
      setSavedNote(
        live
          ? 'Saved.'
          : res.registration.status === 'waitlist'
            ? "You're on the waitlist. We'll let you know if a seat opens."
            : res.paymentRequired
              ? HELD_NOTE
              : "You're in. See you there.",
      );
      onChanged();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'category_full') onChanged();
      setErrorCode(e instanceof ApiError ? e.code : null);
      setError(e instanceof ApiError ? e.message : 'Could not save your RSVP');
    },
  });

  const cancel = useMutation({
    mutationFn: () => rsvpApi.cancel(eventId),
    onSuccess: (res) => {
      setError(null);
      setSavedNote(
        res.paymentStatus === 'refund_due'
          ? 'Your RSVP is cancelled. An admin will refund your payment.'
          : 'Your RSVP is cancelled.',
      );
      onChanged();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not cancel'),
  });

  const pay = useMutation({
    mutationFn: () => rsvpApi.checkout(eventId),
    onSuccess: (res) => {
      if (res.checkoutUrl) window.location.assign(res.checkoutUrl);
      else setError(res.message ?? 'Card payment is not available right now. An admin will be in touch.');
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not start the payment'),
  });

  const categories = summary.categories;
  const category = categories.find((c) => c.id === categoryId) ?? null;
  // Every category is taken (the member's own aside): there's nothing to pick.
  const myCategory = summary.myStatus === 'registered' ? summary.myCategoryId : null;
  const allCategoriesFull =
    categories.length > 0 && categories.every((c) => c.full && c.id !== myCategory);

  // The seat is held until it's paid. "Pay now" only appears when card payment
  // is set up; otherwise an admin records the payment.
  const savedText =
    savedNote === HELD_NOTE
      ? summary.payment.checkoutAvailable
        ? 'Your seat is held. Tap "Pay now" above to confirm it.'
        : 'Your seat is held until an admin marks it paid.'
      : savedNote;
  const errorText =
    error && errorCode === 'category_full' && allCategoriesFull ? ALL_FULL_MESSAGE : error;

  function submit() {
    setError(null);
    setErrorCode(null);
    setSavedNote(null);
    const body: RsvpBody = { labelImageUrl: labelImageUrl.trim(), notes };
    if (!isDraftEmpty(wine)) {
      const done = completeWine(wine, summary.worldScope);
      if (!done.wine) {
        setError(`Please fill in: ${done.missing.join(', ')}.`);
        return;
      }
      body.wine = done.wine;
      if (categories.length > 0 && !categoryId) {
        setError(
          allCategoriesFull ? ALL_FULL_MESSAGE : 'Please pick which category your wine is for.',
        );
        return;
      }
    }
    if (categories.length > 0 && categoryId) body.categoryId = categoryId;
    save.mutate(body);
  }

  // The cancel question, worded for this seat. Its own buttons ("Keep my seat"
  // / "Yes, cancel"), not a generic confirm: two buttons both saying "Cancel"
  // is exactly the wrong question to put to someone on a phone.
  const paid = summary.myPaymentStatus === 'paid';
  const forfeit = paid && !summary.cancellation.refundable;
  const cancelCopy = (() => {
    const { cutoffDays, refundable, refundableUntil } = summary.cancellation;
    if (summary.myStatus === 'waitlist') return "You'll come off the waitlist.";
    let text = 'Your seat will go to the next person on the waitlist.';
    if (paid && refundable) text += ' You paid for this seat, so an admin will refund you.';
    else if (forfeit)
      text += ` It's within ${cutoffDays ?? 0} day${cutoffDays === 1 ? '' : 's'} of the tasting, so your payment can't be refunded.`;
    else if (summary.payment.required && cutoffDays != null && refundableUntil)
      text += ` Cancel by ${shortDay(refundableUntil)} for a refund.`;
    return text;
  })();

  const header = (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-sm uppercase tracking-wide text-gold-400">Your RSVP</h2>
      {live && <StatusPill summary={summary} />}
    </div>
  );

  const paymentReturnBanner = paymentReturn ? (
    <div
      className={`mb-4 flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
        paymentReturn === 'success'
          ? 'border-success/40 bg-success/5 text-cream-100'
          : 'border-warning/40 bg-warning/5 text-cream-100'
      }`}
    >
      <span>
        {paymentReturn === 'success'
          ? 'Thank you, your payment went through. It can take a minute to show as paid.'
          : "Payment was cancelled. Your seat isn't confirmed until it's paid."}
      </span>
      <button type="button" className="text-xs text-cream-300/60" onClick={dismissPaymentReturn}>
        Dismiss
      </button>
    </div>
  ) : null;

  // Can't RSVP: say why, and nothing else to fill in.
  if (summary.blockedReason && !live) {
    return (
      <div className="card mb-6">
        {header}
        <p className="mb-3 text-sm text-cream-200">
          {RSVP_BLOCKED_MESSAGES[summary.blockedReason]}
        </p>
        {summary.blockedReason === 'dues_outstanding' && (
          <Link to="/account" className="btn-primary mb-3 inline-block">
            Pay your dues
          </Link>
        )}
        <GuestsLine />
      </div>
    );
  }
  if (!summary.open) {
    return (
      <div className="card mb-6">
        {header}
        <p className="text-sm text-cream-300/60">RSVPs are closed for this event.</p>
        {live && summary.myWineName && (
          <p className="mt-2 text-sm text-cream-200">You're bringing: {summary.myWineName}</p>
        )}
      </div>
    );
  }

  const unpaid = summary.myStatus === 'registered' && summary.myPaymentStatus === 'unpaid';
  const full = summary.spotsLeft === 0;

  return (
    <div className="card mb-6">
      {header}
      {paymentReturnBanner}

      {summary.registeredByAdmin && live && (
        <p className="mb-3 text-xs text-cream-300/60">An admin registered you for this one.</p>
      )}

      {summary.payment.required && (
        <PaymentBox
          summary={summary}
          unpaid={unpaid}
          paying={pay.isPending}
          onPay={() => pay.mutate()}
        />
      )}

      {summary.rules && (
        <div className="mb-5 rounded-md border border-gold-600/30 bg-gold-600/5 px-4 py-3">
          <h3 className="mb-1 text-xs uppercase tracking-wide text-gold-400">The rules</h3>
          <p className="whitespace-pre-line text-sm text-cream-100">{summary.rules}</p>
        </div>
      )}

      <div className="space-y-5">
        {categories.length > 0 && (
          <CategoryPicker
            categories={categories}
            value={categoryId}
            mine={myCategory}
            onChange={setCategoryId}
          />
        )}
        {allCategoriesFull && (
          <p className="-mt-2 text-sm text-cream-200">{ALL_FULL_MESSAGE}</p>
        )}

        <div>
          <h3 className="mb-1 font-display text-lg text-cream-50">What are you bringing?</h3>
          <p className="mb-3 text-xs text-cream-300/60">
            {live
              ? 'You can change this any time before the tasting.'
              : "Add it now or come back later. We'll remind you."}
          </p>
          {!summary.myWine && summary.myWineName && (
            <p className="mb-3 rounded-md border border-ink-800 px-3 py-2 text-sm text-cream-200">
              You told us: <span className="text-cream-50">{summary.myWineName}</span>. Please
              enter it below so it lands in the Cellar properly.
            </p>
          )}
          {summary.myWine && summary.myWineReviewed && (
            <p className="mb-3 text-xs text-success">Checked by an admin ✓</p>
          )}
          <StructuredWineFields
            value={wine}
            onChange={setWine}
            worldScope={summary.worldScope}
            categoryColor={category?.color}
          />
        </div>

        <div>
          <span className="mb-1 block text-sm text-cream-200">Photo of the label (optional)</span>
          <ImageInput value={labelImageUrl} onChange={setLabelImageUrl} />
        </div>

        <label className="block">
          <span className="mb-1 block text-sm text-cream-200">Notes / food preferences</span>
          <textarea
            className="field min-h-[70px]"
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>

        <GuestsLine />

        {errorText && (
          <p role="alert" className="text-sm text-wine-300">
            {errorText}
          </p>
        )}
        {savedText && !errorText && <p className="text-sm text-gold-200">{savedText}</p>}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button
            className="py-3 text-base sm:py-2 sm:text-sm"
            loading={save.isPending}
            onClick={submit}
          >
            {live ? 'Save changes' : full ? 'Join the waitlist' : "RSVP — I'm coming"}
          </Button>
          {!live && summary.spotsLeft != null && (
            <span className="text-xs text-cream-300/60">
              {full
                ? 'The tasting is full. You can join the waitlist.'
                : `${summary.spotsLeft} seat${summary.spotsLeft === 1 ? '' : 's'} left`}
            </span>
          )}
          {live && (
            <Button variant="quiet" loading={cancel.isPending} onClick={() => setAskCancel(true)}>
              {summary.myStatus === 'waitlist' ? 'Leave the waitlist' : 'Cancel my RSVP'}
            </Button>
          )}
        </div>
        {live && summary.myStatus === 'registered' && <CutoffNote summary={summary} />}
      </div>

      <Dialog
        open={askCancel}
        onClose={() => setAskCancel(false)}
        title={summary.myStatus === 'waitlist' ? 'Leave the waitlist?' : 'Cancel your RSVP?'}
        description={cancelCopy}
        size="sm"
        footer={
          <>
            <Button variant="quiet" onClick={() => setAskCancel(false)}>
              {summary.myStatus === 'waitlist' ? 'Stay on it' : 'Keep my seat'}
            </Button>
            <Button
              variant="danger"
              loading={cancel.isPending}
              onClick={() => {
                setAskCancel(false);
                cancel.mutate();
              }}
            >
              {forfeit ? 'Yes, cancel without refund' : 'Yes, cancel'}
            </Button>
          </>
        }
      />
    </div>
  );
}

/** Marks the "seat held, payment due" note; its wording follows `checkoutAvailable`. */
const HELD_NOTE = 'held';

const ALL_FULL_MESSAGE = 'Every wine category is full. Please contact an admin.';

function StatusPill({ summary }: { summary: RsvpSummaryView }) {
  const registered = summary.myStatus === 'registered';
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs uppercase tracking-wide ${
        registered ? 'bg-gold-600 text-on-gold' : 'border border-gold-600/50 text-gold-200'
      }`}
    >
      {registered ? "You're attending" : "You're on the waitlist"}
      {summary.myGuests > 0 && ` (+${summary.myGuests})`}
    </span>
  );
}

function GuestsLine() {
  return <p className="text-sm text-cream-300/70">Bringing a guest? Contact an admin.</p>;
}

function PaymentBox({
  summary,
  unpaid,
  paying,
  onPay,
}: {
  summary: RsvpSummaryView;
  unpaid: boolean;
  paying: boolean;
  onPay: () => void;
}) {
  const price = summary.payment.price ? money(summary.payment.price) : null;
  const status = summary.myPaymentStatus;
  if (unpaid) {
    return (
      <div className="mb-5 rounded-md border border-warning/40 bg-warning/5 px-4 py-3">
        <p className="text-sm text-cream-50">
          Your seat is held but <strong>not confirmed</strong> until it's paid
          {price ? ` (${price})` : ''}.
        </p>
        {summary.payment.checkoutAvailable ? (
          <Button className="mt-3 w-full py-3 text-base sm:w-auto" loading={paying} onClick={onPay}>
            Pay now{price ? ` · ${price}` : ''}
          </Button>
        ) : (
          <p className="mt-1 text-xs text-cream-300/70">
            An admin will be in touch about payment.
          </p>
        )}
      </div>
    );
  }
  if (status === 'paid' || status === 'waived' || status === 'refund_due' || status === 'refunded') {
    return (
      <p className="mb-5 text-sm text-cream-200">
        Payment: <span className="text-gold-200">{REGISTRATION_PAYMENT_LABELS[status]}</span>
        {status === 'paid' && ' ✓'}
      </p>
    );
  }
  return (
    <p className="mb-5 text-sm text-cream-200">
      {price ? `${price} per seat. ` : ''}Your seat is confirmed once it's paid.
    </p>
  );
}

function CutoffNote({ summary }: { summary: RsvpSummaryView }) {
  const { cutoffDays, refundableUntil, refundable } = summary.cancellation;
  if (!summary.payment.required || cutoffDays == null) return null;
  return (
    <p className="text-xs text-cream-300/60">
      {refundable && refundableUntil
        ? `Need to cancel? Do it by ${shortDay(refundableUntil)} for a refund. After that the seat can't be refunded.`
        : `It's within ${cutoffDays} day${cutoffDays === 1 ? '' : 's'} of the tasting, so a paid seat can no longer be refunded.`}
    </p>
  );
}

function CategoryPicker({
  categories,
  value,
  mine,
  onChange,
}: {
  categories: WineCategoryView[];
  value: string | null;
  /** The category the member already holds a seat in — never "full" to them. */
  mine: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-2 font-display text-lg text-cream-50">Which category?</legend>
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
        {categories.map((c) => {
          const blocked = c.full && c.id !== mine;
          const selected = value === c.id;
          return (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={blocked}
              onClick={() => onChange(c.id)}
              className={`flex items-center justify-between gap-3 rounded-md border px-4 py-3 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50 ${
                selected
                  ? 'border-gold-400 bg-gold-600/15 text-gold-200'
                  : 'border-ink-800 text-cream-200 hover:border-gold-600/50'
              }`}
            >
              <span>{c.name}</span>
              <span className="shrink-0 text-xs text-cream-300/70">
                {c.full && c.id === mine
                  ? 'Your seat'
                  : c.remaining == null
                    ? 'Open'
                    : blocked
                      ? 'Full'
                      : `${c.remaining} left`}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
